import React from "react";
import PropTypes from "prop-types";
import { Check } from "@mui/icons-material";

import "./UIStepper.scss";

const getStepState = ({ index, activeStep, step, completedSteps }) => {
  if (step.status) return step.status;
  if (completedSteps.includes(step.id) || completedSteps.includes(index)) {
    return "completed";
  }
  if (index < activeStep) return "completed";
  if (index === activeStep) return "active";
  return "upcoming";
};

const UIStepper = ({
  steps,
  activeStep,
  completedSteps,
  onStepClick,
  className,
  ariaLabel,
}) => {
  const safeActiveStep = steps.length
    ? Math.min(Math.max(activeStep, 0), steps.length - 1)
    : 0;
  const rootClassName = ["ui-stepper", className].filter(Boolean).join(" ");
  const stepperCount = Math.max(steps.length, 1);

  return (
    <nav
      className={rootClassName}
      aria-label={ariaLabel}
      style={{ "--stepper-count": stepperCount }}
    >
      <ol className="ui-stepper__list">
        {steps.map((step, index) => {
          const state = getStepState({
            index,
            activeStep: safeActiveStep,
            step,
            completedSteps,
          });
          const stepControlIsClickable =
            Boolean(onStepClick)  && !step.disabled;
          const StepTag = stepControlIsClickable ? "button" : "div";

          return (
            <li
              key={step.id}
              className={`ui-stepper__item ui-stepper__item--${state}`}
              title={step.hint || undefined}
            >
              <StepTag
                type={stepControlIsClickable ? "button" : undefined}
                className="ui-stepper__control"
                onClick={
                  stepControlIsClickable
                    ? () => onStepClick(step, index)
                    : undefined
                }
                aria-current={state === "active" ? "step" : undefined}
                aria-disabled={
                  step.disabled || (Boolean(onStepClick))
                    ? true
                    : undefined
                }
              >
                <span className="ui-stepper__indicator" aria-hidden="true">
                  {state === "completed" ? (
                    <Check className="ui-stepper__check-icon" />
                  ) : (
                    step.icon || index + 1
                  )}
                </span>
                <span className="ui-stepper__label">{step.label}</span>
              </StepTag>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

UIStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      disabled: PropTypes.bool,
      /** Shown on hover, e.g. what an `error` / `warning` step still misses. */
      hint: PropTypes.string,
      /**
       * `error`: fields still to fill after a failed submission.
       * `warning`: visited but not complete yet (e.g. a draft step still missing something).
       */
      status: PropTypes.oneOf(["completed", "active", "upcoming", "error", "warning"]),
    })
  ).isRequired,
  activeStep: PropTypes.number,
  completedSteps: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onStepClick: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

UIStepper.defaultProps = {
  activeStep: 0,
  completedSteps: [],
  onStepClick: undefined,
  className: "",
  ariaLabel: "Progress",
};

export default UIStepper;
